<template>
  <v-row class="fill-height">
    <!-- Left Pane: Folder Tree (Hidden on mobile) -->
    <v-col cols="12" md="4" lg="3" class="d-none d-md-block border-right">
      <v-card
        flat
        class="fill-height"
        style="border-right: 1px solid rgba(0, 0, 0, 0.12)"
      >
        <v-card-title class="subtitle-1 font-weight-bold">Kansiot</v-card-title>
        <v-treeview
          :items="categoryTree"
          activatable
          item-key="id"
          :active="activeNodes"
          @update:active="onTreeSelection"
          :open.sync="openNodes"
          dense
          hoverable
        >
          <template v-slot:prepend="{ item, open }">
            <v-icon>mdi {{ open ? "mdi-folder-open" : "mdi-folder" }}</v-icon>
          </template>
          <template v-slot:label="{ item }">
            <div class="text-truncate" :title="item.name">
              {{ item.name }}
            </div>
          </template>
        </v-treeview>
      </v-card>
    </v-col>

    <!-- Right Pane: File List -->
    <v-col cols="12" md="8" lg="9">
      <v-card flat>
        <!-- Breadcrumbs / Navigation -->
        <v-card-title class="d-flex align-center py-2 pl-0" v-if="!search">
          <v-btn
            icon
            @click="navigateUp"
            :disabled="!activeCategoryId"
            class="mr-2"
          >
            <v-icon>mdi-arrow-up</v-icon>
          </v-btn>

          <v-breadcrumbs :items="breadcrumbs" class="pa-0">
            <template v-slot:item="{ item }">
              <v-breadcrumbs-item
                :disabled="item.disabled"
                @click="selectCategory(item.id)"
                class="cursor-pointer"
              >
                {{ item.text }}
              </v-breadcrumbs-item>
            </template>
          </v-breadcrumbs>

          <v-spacer></v-spacer>

          <!-- Add buttons (visible in edit mode) -->
          <div v-if="editMode">
            <v-btn icon @click="$emit('add-category', activeCategoryId)">
              <v-icon>mdi-folder-plus</v-icon>
            </v-btn>
            <v-btn
              icon
              @click="$emit('add-file', activeCategoryId)"
              :disabled="!activeCategoryId"
            >
              <v-icon>mdi-file-plus</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-card-title class="d-flex align-center py-2 pl-0" v-else>
          <span class="subtitle-1">Hakutulokset: "{{ search }}"</span>
        </v-card-title>
        <v-divider></v-divider>

        <!-- Content -->
        <v-list two-line>
          <template v-if="!search">
            <!-- Back button for mobile (if not at root) -->
            <v-list-item
              v-if="activeCategoryId"
              @click="navigateUp"
              class="d-md-none"
            >
              <v-list-item-avatar>
                <v-icon>mdi-arrow-left</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>Takaisin</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <!-- Sub-folders -->
            <v-list-item
              v-for="cat in currentSubCategories"
              :key="'c-' + cat.id"
              @click="selectCategory(cat.id)"
            >
              <v-list-item-avatar>
                <v-icon class="amber lighten-4 amber--text text--darken-3"
                  >mdi-folder</v-icon
                >
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ cat.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ cat.description }}
                </v-list-item-subtitle>
                <div
                  v-if="
                    showDepartments &&
                    cat.departments &&
                    cat.departments.length > 0
                  "
                  class="mt-1"
                >
                  <v-chip
                    x-small
                    class="mr-1"
                    v-for="dept in cat.departments"
                    :key="dept"
                  >
                    {{ getDepartmentLabel(dept) }}
                  </v-chip>
                </div>
              </v-list-item-content>
              <v-list-item-action v-if="editMode">
                <v-btn icon @click.stop="$emit('delete-category', cat)">
                  <v-icon color="error">mdi-close</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>

            <!-- Files -->
            <v-list-item
              v-for="file in currentFiles"
              :key="'f-' + file.id"
              @click="$emit('open-file', file)"
            >
              <v-list-item-avatar>
                <v-progress-circular
                  v-if="file._opening"
                  indeterminate
                  size="24"
                  color="primary"
                ></v-progress-circular>
                <v-icon v-else color="primary"
                  >mdi {{ getFileIcon(file.fileExtension) }}</v-icon
                >
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  {{
                    file.customFilename
                      ? file.customFilename + "." + file.fileExtension
                      : file.filename
                  }}
                </v-list-item-title>
                <v-list-item-subtitle
                  :style="showFullDescription ? 'white-space: normal;' : ''"
                >
                  {{ $formatDateAndHourMinute(file.createdAt) }}
                  <span v-if="file.description">
                    &bull; {{ file.description }}
                  </span>
                </v-list-item-subtitle>
                <div
                  v-if="
                    showDepartments &&
                    file.departments &&
                    file.departments.length > 0
                  "
                  class="mt-1"
                >
                  <v-chip
                    x-small
                    class="mr-1"
                    v-for="dept in file.departments"
                    :key="dept"
                  >
                    {{ getDepartmentLabel(dept) }}
                  </v-chip>
                </div>
              </v-list-item-content>
              <v-list-item-action v-if="editMode">
                <div class="d-flex">
                  <v-btn icon @click.stop="$emit('edit-file', file)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon @click.stop="$emit('delete-file', file)">
                    <v-icon color="error">mdi-close</v-icon>
                  </v-btn>
                </div>
              </v-list-item-action>
            </v-list-item>

            <v-list-item
              v-if="
                currentSubCategories.length === 0 && currentFiles.length === 0
              "
            >
              <v-list-item-content>
                <v-list-item-title class="grey--text text-center"
                  >Tyhjä kansio</v-list-item-title
                >
              </v-list-item-content>
            </v-list-item>
          </template>

          <template v-else>
            <!-- Search Results: Categories -->
            <v-list-item
              v-for="cat in searchResults.categories"
              :key="'search-c-' + cat.id"
              @click="selectCategory(cat.id)"
            >
              <v-list-item-avatar>
                <v-icon class="amber lighten-4 amber--text text--darken-3"
                  >mdi-folder</v-icon
                >
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ cat.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ cat._categoryPath ? cat._categoryPath + " / " : ""
                  }}{{ cat.name }}
                  <span v-if="showDepartments && cat.departments">
                    &bull;
                    <v-chip
                      x-small
                      class="mr-1"
                      v-for="dept in cat.departments"
                      :key="dept"
                    >
                      {{ getDepartmentLabel(dept) }}
                    </v-chip>
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <!-- Search Results: Files -->
            <v-list-item
              v-for="file in searchResults.files"
              :key="'search-f-' + file.id"
              @click="$emit('open-file', file)"
            >
              <v-list-item-avatar>
                <v-icon color="primary"
                  >mdi {{ getFileIcon(file.fileExtension) }}</v-icon
                >
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  {{
                    file.customFilename
                      ? file.customFilename + "." + file.fileExtension
                      : file.filename
                  }}
                </v-list-item-title>
                <v-list-item-subtitle
                  :style="showFullDescription ? 'white-space: normal;' : ''"
                >
                  {{ file._categoryPath }} &bull;
                  {{ $formatDateAndHourMinute(file.createdAt) }}
                  <span v-if="file.description">
                    &bull; {{ file.description }}
                  </span>
                  <span v-if="showDepartments && file.departments">
                    &bull;
                    <v-chip
                      x-small
                      class="mr-1"
                      v-for="dept in file.departments"
                      :key="dept"
                    >
                      {{ getDepartmentLabel(dept) }}
                    </v-chip>
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="editMode">
                <div class="d-flex">
                  <v-btn icon @click.stop="$emit('edit-file', file)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon @click.stop="$emit('delete-file', file)">
                    <v-icon color="error">mdi-close</v-icon>
                  </v-btn>
                </div>
              </v-list-item-action>
            </v-list-item>

            <v-list-item
              v-if="
                searchResults.files.length === 0 &&
                searchResults.categories.length === 0
              "
            >
              <v-list-item-content>
                <v-list-item-title class="grey--text text-center"
                  >Ei hakutuloksia</v-list-item-title
                >
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-list>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { orderBy } from "lodash";

export default {
  name: "TiedostopankkiExplorer",
  props: {
    categories: { type: Array, default: () => [] },
    files: { type: Array, default: () => [] },
    editMode: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    search: { type: String, default: "" },
    showDepartments: { type: Boolean, default: false },
    showFullDescription: { type: Boolean, default: false },
  },
  data() {
    return {
      activeCategoryId: null,
      activeNodes: [], // For v-treeview
      openNodes: [],
    };
  },
  computed: {
    categoryTree() {
      // Build a tree of categories only
      const buildTree = (parentId) => {
        const children = this.categories
          .filter((c) => c.parentCategoryId === parentId)
          .map((c) => ({
            ...c,
            children: buildTree(c.id),
          }));

        return children.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: "base",
          })
        );
      };
      return buildTree(null);
    },
    activeCategory() {
      return this.categories.find((c) => c.id === this.activeCategoryId);
    },
    currentSubCategories() {
      return this.categories
        .filter((c) => c.parentCategoryId === this.activeCategoryId)
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: "base",
          })
        );
    },
    currentFiles() {
      return orderBy(
        this.files.filter((f) => f.categoryId === this.activeCategoryId),
        "filename"
      );
    },
    breadcrumbs() {
      const path = [];
      let current = this.activeCategory;
      while (current) {
        path.unshift({
          text: current.name,
          disabled: false,
          id: current.id,
        });
        current = this.categories.find(
          (c) => c.id === current.parentCategoryId
        );
      }
      // Add root
      path.unshift({
        text: "Tiedostopankki",
        disabled: false,
        id: null,
      });

      // Disable the last one (current)
      if (path.length > 0) {
        path[path.length - 1].disabled = true;
      }

      return path;
    },
    searchResults() {
      if (!this.search) return { files: [], categories: [] };
      const searchLower = this.search.toLowerCase();

      const files = this.files
        .filter(
          (f) =>
            f.filename.toLowerCase().includes(searchLower) ||
            (f.description &&
              f.description.toLowerCase().includes(searchLower)) ||
            (f.customFilename &&
              f.customFilename.toLowerCase().includes(searchLower))
        )
        .map((f) => ({
          ...f,
          _categoryPath: this.getCategoryPath(f.categoryId),
        }));

      const categories = this.categories
        .filter((c) => c.name.toLowerCase().includes(searchLower))
        .map((c) => ({
          ...c,
          _categoryPath: this.getCategoryPath(c.parentCategoryId),
        }));

      return { files, categories };
    },
  },
  watch: {
    activeCategoryId(newVal) {
      // Sync with treeview selection
      const currentActive =
        this.activeNodes.length > 0 ? this.activeNodes[0] : null;
      if (newVal !== currentActive) {
        if (newVal) {
          this.expandToCategory(newVal);
          // Use nextTick to ensure treeview is ready to accept selection after expansion
          this.$nextTick(() => {
            this.activeNodes = [newVal];
          });
        } else {
          this.activeNodes = [];
        }
      }
    },
  },
  methods: {
    selectCategory(id) {
      this.activeCategoryId = id;
    },
    onTreeSelection(nodes) {
      // Only update if we have a definitive selection change from the tree
      const newId = nodes.length > 0 ? nodes[0] : null;

      if (newId === null) {
        // Prevent deselection by clicking the active node again
        this.$nextTick(() => {
          if (this.activeCategoryId) {
            this.activeNodes = [this.activeCategoryId];
          }
        });
        return;
      }

      if (this.activeCategoryId !== newId) {
        this.activeCategoryId = newId;
      }
    },
    expandToCategory(id) {
      const parents = [];
      let current = this.categories.find((c) => c.id === id);
      while (current && current.parentCategoryId) {
        parents.push(current.parentCategoryId);
        current = this.categories.find(
          (c) => c.id === current.parentCategoryId
        );
      }
      // Add these to openNodes if not present
      this.openNodes = [...new Set([...this.openNodes, ...parents])];
    },
    navigateUp() {
      if (this.activeCategory && this.activeCategory.parentCategoryId) {
        this.activeCategoryId = this.activeCategory.parentCategoryId;
      } else {
        this.activeCategoryId = null;
      }
    },
    getCategoryPath(categoryId) {
      const path = [];
      let current = this.categories.find((c) => c.id === categoryId);
      while (current) {
        path.unshift(current.name);
        current = this.categories.find(
          (c) => c.id === current.parentCategoryId
        );
      }
      return path.join(" / ");
    },
    getFileIcon(extension) {
      const icons = {
        pdf: "mdi-file-document",
        jpg: "mdi-file-image",
        jpeg: "mdi-file-image",
        png: "mdi-file-image",
        gif: "mdi-file-image",
        bmp: "mdi-file-image",
        mp3: "mdi-file-music",
        wav: "mdi-file-music",
        mp4: "mdi-file-video",
        mov: "mdi-file-video",
        avi: "mdi-file-video",
        doc: "mdi-file-word",
        docx: "mdi-file-word",
        xls: "mdi-file-excel",
        xlsx: "mdi-file-excel",
        ppt: "mdi-file-powerpoint",
        pptx: "mdi-file-powerpoint",
      };
      return icons[(extension || "").toLowerCase()] || "mdi-file";
    },
    getDepartmentLabel(value) {
      const map = {
        halytysosasto: "Hälytysosasto",
        naisosasto: "Tukiosasto",
        hallitus: "Hallitus",
      };
      return map[value] || value;
    },
  },
};
</script>

<style scoped>
.border-right {
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
